import { useEffect, useMemo, useState, type ReactElement } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';
import { LoadingBlock } from '../../components/Spinner';
import { formatRange } from '../../lib/eventDates';

/**
 * Admin → Operations → Date recalcs.
 *
 * Audit log of every "Recalculate date" admin click. Lets the admin review:
 *   - Which posts had their date actually changed by the recalc.
 *   - When the original (now-replaced) events were first extracted —
 *     surfaces a "did the date-detection algorithm change between then and
 *     now?" signal vs. "the algorithm still gets this kind of post wrong."
 *
 * Source: `GET /api/mgmt/events/recalculations`. Newest first.
 */

interface RecalcSnapshot {
  rowKey: string;
  partitionKey: string;
  title: string;
  startUtc: string;
  endUtc?: string;
  isAllDay?: boolean;
  status?: string;
  isRecurringInstance?: boolean;
  recurringSeriesId?: string;
  dateConfidence?: string;
  updatedAt?: string;
  storageTimestamp?: string;
}

interface RecalcRow {
  partitionKey: string;
  rowKey: string;
  triggeredAt: string;
  triggeredBy?: string;
  rawId: string;
  rawPartitionKey: string;
  rawSourceType?: string;
  rawSourceId?: string;
  rawPostedAt?: string;
  preEventCount: number;
  postEventCount: number;
  preEarliestExtractedAt?: string;
  dateChanged?: boolean;
  completedAt?: string;
  completionError?: string;
  preEvents: RecalcSnapshot[];
  postEvents: RecalcSnapshot[];
}

interface RecalcResponse {
  scannedPartitions: string[];
  totalRows: number;
  matched: number;
  returned: number;
  items: RecalcRow[];
}

type Filter = 'changed' | 'all' | 'unchanged' | 'pending';

function formatDateTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/** Source-post metadata keeps its pre-existing reporting timezone and style. */
function formatSourcePostDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    timeZone: 'America/New_York', weekday: 'short', month: 'short',
    day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function timeAgo(iso?: string): string {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function statusBadge(row: RecalcRow): ReactElement {
  if (!row.completedAt) {
    return <span className="badge text-bg-warning text-dark">Pending…</span>;
  }
  if (row.completionError) {
    return <span className="badge text-bg-danger" title={row.completionError}>Error</span>;
  }
  if (row.dateChanged) {
    return <span className="badge text-bg-success">Date changed</span>;
  }
  return <span className="badge text-bg-secondary">Unchanged</span>;
}

export default function AdminRecalculationsPage(): ReactElement {
  const [rows, setRows] = useState<RecalcRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('changed');
  const [search, setSearch] = useState<string>('');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set<string>());

  async function load(): Promise<void> {
    setError(null);
    try {
      // includePending=1 so we can offer a "Pending" filter view; the
      // default filter ("Changed") will hide them anyway.
      const data = await api.get<RecalcResponse>(
        '/api/mgmt/events/recalculations?limit=500&includePending=1'
      );
      setRows(data.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setRows([]);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const summary = useMemo(() => {
    const r = rows ?? [];
    return {
      total: r.length,
      changed: r.filter((x) => x.completedAt && x.dateChanged === true).length,
      unchanged: r.filter((x) => x.completedAt && x.dateChanged === false).length,
      pending: r.filter((x) => !x.completedAt).length,
      errors: r.filter((x) => x.completedAt && x.completionError).length
    };
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      switch (filter) {
        case 'changed':
          if (!(r.completedAt && r.dateChanged === true)) return false;
          break;
        case 'unchanged':
          if (!(r.completedAt && r.dateChanged === false)) return false;
          break;
        case 'pending':
          if (r.completedAt) return false;
          break;
        case 'all':
        default:
          break;
      }
      if (q) {
        const haystack = [
          r.rawId,
          r.rawSourceType,
          r.rawSourceId,
          r.triggeredBy,
          ...r.preEvents.map((s) => s.title),
          ...r.postEvents.map((s) => s.title)
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filter, search]);

  function toggleExpanded(key: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <>
      <AdminLayout>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <h1 className="h3 mb-1">Date recalculations</h1>
            <p className="text-body-secondary small mb-0">
              Audit log of every &ldquo;Recalculate date&rdquo; click. The default view shows only
              recalcs that actually changed the event&rsquo;s date — useful for spotting whether the
              date-detection algorithm has improved over time, or whether specific post styles still
              get parsed wrong.
            </p>
          </div>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => void load()}>
            <i className="bi bi-arrow-clockwise me-1" aria-hidden="true" />
            Refresh
          </button>
        </div>

        {rows && (
          <div className="row g-2 mb-3">
            <SummaryCard label="Total" value={summary.total} muted />
            <SummaryCard label="Date changed" value={summary.changed} cls="text-bg-success" />
            <SummaryCard label="Unchanged" value={summary.unchanged} cls="text-bg-secondary" />
            <SummaryCard label="Pending" value={summary.pending} cls="text-bg-warning text-dark" />
            <SummaryCard label="Errors" value={summary.errors} cls="text-bg-danger" />
          </div>
        )}

        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <div className="btn-group btn-group-sm" role="group" aria-label="Filter by recalc outcome">
            {(['changed', 'unchanged', 'pending', 'all'] as Filter[]).map((f) => (
              <button
                key={f}
                type="button"
                className={`btn btn-outline-secondary${filter === f ? ' active' : ''}`}
                aria-pressed={filter === f}
                onClick={() => setFilter(f)}
              >
                {f === 'changed' && 'Date changed'}
                {f === 'unchanged' && 'Unchanged'}
                {f === 'pending' && 'Pending'}
                {f === 'all' && 'All'}
              </button>
            ))}
          </div>
          <input
            type="search"
            className="form-control form-control-sm"
            style={{ maxWidth: '24rem' }}
            placeholder="Search rawId, source, title, who…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search recalculations"
          />
        </div>

        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        {rows === null && !error && <LoadingBlock label="Loading recalculations…" />}

        {rows && filtered.length === 0 && (
          <p className="text-body-secondary">No recalculations match your filters.</p>
        )}

        {filtered.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th scope="col">Triggered</th>
                  <th scope="col">By</th>
                  <th scope="col">Status</th>
                  <th scope="col">Source</th>
                  <th scope="col">Originally extracted</th>
                  <th scope="col">Pre / Post</th>
                  <th scope="col" className="text-end">Details</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const key = `${r.partitionKey}/${r.rowKey}`;
                  const isOpen = expanded.has(key);
                  return (
                    <Row
                      key={key}
                      row={r}
                      isOpen={isOpen}
                      onToggle={() => toggleExpanded(key)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AdminLayout>
    </>
  );
}

function Row({ row, isOpen, onToggle }: { row: RecalcRow; isOpen: boolean; onToggle: () => void }): ReactElement {
  const sourceLabel = row.rawSourceType
    ? `${row.rawSourceType}${row.rawSourceId ? ` · ${row.rawSourceId}` : ''}`
    : '—';
  return (
    <>
      <tr>
        <td>
          <span title={formatDateTime(row.triggeredAt)}>{timeAgo(row.triggeredAt)}</span>
        </td>
        <td className="small text-break">
          {row.triggeredBy || <span className="text-body-secondary">—</span>}
        </td>
        <td>{statusBadge(row)}</td>
        <td className="small text-break">
          <div>{sourceLabel}</div>
          <div className="text-body-secondary" style={{ fontSize: '0.75rem' }}>
            raw <code>{row.rawId}</code>
          </div>
        </td>
        <td className="small">
          {row.preEarliestExtractedAt ? (
            <span title={formatDateTime(row.preEarliestExtractedAt)}>
              {timeAgo(row.preEarliestExtractedAt)}
            </span>
          ) : (
            <span className="text-body-secondary">—</span>
          )}
        </td>
        <td className="small">
          {row.preEventCount} → {row.postEventCount}
        </td>
        <td className="text-end">
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-label={isOpen ? 'Hide event details' : 'Show event details'}
          >
            <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true" />
          </button>
        </td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={7} className="bg-body-tertiary">
            <DiffPanel row={row} />
          </td>
        </tr>
      )}
    </>
  );
}

function DiffPanel({ row }: { row: RecalcRow }): ReactElement {
  return (
    <div className="row g-3">
      <div className="col-md-6">
        <h2 className="h6 mb-2">Before recalculation ({row.preEvents.length})</h2>
        <SnapshotList events={row.preEvents} />
      </div>
      <div className="col-md-6">
        <h2 className="h6 mb-2">After recalculation ({row.postEvents.length})</h2>
        {row.completedAt ? (
          <SnapshotList events={row.postEvents} />
        ) : (
          <p className="text-body-secondary small mb-0">
            Awaiting queue worker — re-extraction has not finished yet.
          </p>
        )}
        {row.completionError && (
          <div className="alert alert-danger small mt-2 mb-0" role="alert">
            Re-extraction error: <code>{row.completionError}</code>
          </div>
        )}
      </div>
      {row.rawPostedAt && (
        <div className="col-12">
          <p className="small text-body-secondary mb-0">
            Source post published: <span title={row.rawPostedAt}>{formatSourcePostDate(row.rawPostedAt)}</span>
          </p>
        </div>
      )}
    </div>
  );
}

function SnapshotList({ events }: { events: RecalcSnapshot[] }): ReactElement {
  if (events.length === 0) {
    return <p className="text-body-secondary small mb-0">(none)</p>;
  }
  return (
    <ul className="list-unstyled mb-0">
      {events.map((e) => (
        <li key={`${e.partitionKey}/${e.rowKey}`} className="mb-2">
          <div className="fw-medium text-break">{e.title || '(untitled)'}</div>
          <div className="small">
            <span className="me-2">
              {formatRange(e, { date: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' } })}
            </span>
            {e.dateConfidence && (
              <span className="badge text-bg-light text-dark me-1">{e.dateConfidence}</span>
            )}
            {e.status && (
              <span className="badge text-bg-light text-dark">{e.status}</span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function SummaryCard({
  label,
  value,
  cls,
  muted
}: {
  label: string;
  value: number;
  cls?: string;
  muted?: boolean;
}): ReactElement {
  return (
    <div className="col-6 col-md-3 col-lg-2">
      <div className={`card border-0 ${muted ? 'bg-body-tertiary' : ''}`}>
        <div className="card-body py-2">
          <div className="small text-body-secondary">{label}</div>
          <div className="d-flex align-items-baseline gap-2">
            <span className="fs-4 fw-semibold">{value}</span>
            {cls && value > 0 && <span className={`badge ${cls}`} aria-hidden="true">&nbsp;</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
